import { TeamPlanStatusDto, TeamPlanStatusFilterDto } from '@/api/generated';

export type AreaTranslation = {
  alerts: {
    count_few?: string;
    count_many?: string;
    count_one: string;
    count_other?: string;
    empty: string;
    items: {
      absenceReturns: AreaAlertText;
      birthdays: AreaAlertText;
      endingContracts: AreaAlertText;
      headcount: AreaAlertText;
      overdueSteps: AreaAlertText;
      plansToApprove: AreaAlertText;
      withoutPlan: AreaAlertText;
    };
    title: string;
  };
  kpi: {
    employees: string;
    openSuccessions: string;
    overdueSteps: string;
    plansToApprove: string;
    withoutPlan: string;
  };
  people: {
    badges: {
      contractEnding: string;
      longAbsence: string;
      panels: string;
      pendingPanel: string;
      trainings: string;
    };
    count: string;
    empty: string;
    filters: {
      planStatus: Record<AreaPlanStatusFilterKey, string>;
      planStatusAll: string;
      planStatusLabel: string;
      risk: Record<'all' | 'high' | 'low' | 'medium', string>;
      riskAll: string;
      riskLabel: string;
      trainingsAll: string;
      trainingsLabel: string;
    };
    headers: {
      department: string;
      employee: string;
      plan: string;
      pmc: string;
      risk: string;
      sop: string;
      status: string;
      succession: string;
    };
    panelDialog: {
      cancel: string;
      date: string;
      feedback: string;
      feedbackPlaceholder: string;
      role: string;
      rolePlaceholder: string;
      save: string;
      title: string;
    };
    planStatus: Record<TeamPlanStatusDto, string>;
    searchPlaceholder: string;
  };
  pipeline: {
    buckets: {
      from1To2Years: string;
      from3To5Years: string;
      readyNow: string;
      upTo1Year: string;
    };
    title: string;
    total: string;
    totalValue: string;
  };
  pmc: {
    average: string;
    title: string;
  };
  risk: {
    chips: {
      risk: Record<'high' | 'low' | 'medium', string>;
      succession: Record<'dev' | 'none' | 'ready', string>;
    };
    peopleCount: string;
    riskTitle: string;
  };
  stats: {
    backToStart: string;
    sectionTitle: string;
  };
  succession: {
    meta: {
      candidates: string;
      emptyForMonths: string;
    };
    needsIntervention: string;
    positionsCount: string;
    readiness: Record<'ready' | 'upTo2Years' | 'upTo3Years', string>;
    state: Record<'empty' | 'inProgress' | 'ready', string>;
    title: string;
  };
  tabs: {
    dashboard: string;
    matrix: string;
    people: string;
    peopleWithCount: string;
    risk: string;
  };
};

type AreaAlertText = {
  subtitle: string;
  title: string;
};

type AreaPlanStatusFilterKey =
  | typeof TeamPlanStatusFilterDto.All
  | typeof TeamPlanStatusFilterDto.Approved
  | typeof TeamPlanStatusFilterDto.None
  | typeof TeamPlanStatusFilterDto.Overdue
  | typeof TeamPlanStatusFilterDto.Waiting;

export const areaTranslation: AreaTranslation = {
  alerts: {
    count_one: '{{count}} alert',
    count_other: '{{count}} alerts',
    empty: 'Nothing needs your attention in your area.',
    items: {
      absenceReturns: {
        subtitle: 'Returns from long-term absence — worth planning a return conversation',
        title: '{{value}} returns from absence',
      },
      birthdays: {
        subtitle: 'Upcoming birthdays in your area',
        title: '{{value}} birthdays',
      },
      endingContracts: {
        subtitle: 'Contracts ending within the next 14 days',
        title: '{{value}} ending contracts',
      },
      headcount: {
        subtitle: 'Change of {{delta}} compared to 2 weeks ago',
        title: 'Headcount: {{value}}',
      },
      overdueSteps: {
        subtitle: 'Step deadline has passed and the step is not completed in the active cycle',
        title: '{{value}} employees with overdue steps',
      },
      plansToApprove: {
        subtitle: 'Plans ready for realization, waiting for supervisor approval',
        title: '{{value}} plans to approve',
      },
      withoutPlan: {
        subtitle: 'No development plan in the current cycle',
        title: '{{value}} employees without a development plan',
      },
    },
    title: 'Requires HRBP attention',
  },
  kpi: {
    employees: 'Employees in area',
    openSuccessions: 'Open successions',
    overdueSteps: 'Overdue steps',
    plansToApprove: 'Plans to approve',
    withoutPlan: 'Without development plan',
  },
  people: {
    badges: {
      contractEnding: 'Contract ending (under 35 days)',
      longAbsence: 'Long-term absence',
      panels: 'Discussion panels',
      pendingPanel: 'Panel conclusions not filled in yet',
      trainings: 'Completed activities',
    },
    count: '{{shown}} of {{total}} employees',
    empty: 'No results',
    filters: {
      planStatus: {
        [TeamPlanStatusFilterDto.All]: 'All',
        [TeamPlanStatusFilterDto.Approved]: 'Approved',
        [TeamPlanStatusFilterDto.None]: 'No plan',
        [TeamPlanStatusFilterDto.Overdue]: 'Overdue',
        [TeamPlanStatusFilterDto.Waiting]: 'Waiting',
      },
      planStatusAll: 'Any plan status',
      planStatusLabel: 'Plan status',
      risk: {
        all: 'All',
        high: 'High',
        low: 'Low',
        medium: 'Medium',
      },
      riskAll: 'Any attrition risk',
      riskLabel: 'Attrition risk',
      trainingsAll: 'Any activity',
      trainingsLabel: 'Activities',
    },
    headers: {
      department: 'Department / Location',
      employee: 'Employee',
      plan: 'Plan%',
      pmc: 'PMC',
      risk: 'Attrition risk',
      sop: 'SOP',
      status: 'Plan status',
      succession: 'Succession',
    },
    panelDialog: {
      cancel: 'Cancel',
      date: 'Panel date',
      feedback: 'Feedback / Recommendations',
      feedbackPlaceholder: 'Write down the panel conclusions...',
      role: 'Considered position',
      rolePlaceholder: 'e.g. Team Leader',
      save: 'Save conclusions',
      title: 'Enter panel feedback',
    },
    planStatus: {
      [TeamPlanStatusDto.Approved]: 'Approved',
      [TeamPlanStatusDto.Draft]: 'Draft',
      [TeamPlanStatusDto.None]: 'No plan',
      [TeamPlanStatusDto.Overdue]: 'Overdue',
      [TeamPlanStatusDto.Waiting]: 'Waiting',
    },
    searchPlaceholder: 'Search for an employee...',
  },
  pipeline: {
    buckets: {
      from1To2Years: 'Medium horizon (1–2 years)',
      from3To5Years: 'Long horizon (3–5 years)',
      readyNow: 'Ready now (0 months)',
      upTo1Year: 'Short horizon (up to 1 year)',
    },
    title: 'Promotion readiness (pipeline)',
    total: 'Total in pipeline',
    totalValue: '{{value}} employees',
  },
  pmc: {
    average: 'Area PMC average',
    title: 'PMC score distribution',
  },
  risk: {
    chips: {
      risk: {
        high: 'High',
        low: 'Low',
        medium: 'Medium',
      },
      succession: {
        dev: 'In development',
        none: 'None',
        ready: 'Ready',
      },
    },
    peopleCount: '{{count}} people',
    riskTitle: 'Attrition risk',
  },
  stats: {
    backToStart: 'Back to Start',
    sectionTitle: 'Area potential and risk',
  },
  succession: {
    meta: {
      candidates: '{{count}} candidates',
      emptyForMonths: 'No candidates for {{count}} months',
    },
    needsIntervention: 'Requires HR intervention.',
    positionsCount: '{{count}} positions',
    readiness: {
      ready: 'Ready now',
      upTo2Years: '1–2 years',
      upTo3Years: '2–3 years',
    },
    state: {
      empty: 'Pipeline empty',
      inProgress: 'In preparation',
      ready: 'Pipeline ready',
    },
    title: 'Succession',
  },
  tabs: {
    dashboard: 'HRBP dashboard',
    matrix: 'Team matrix',
    people: 'Employees',
    peopleWithCount: 'Employees ({{value}})',
    risk: 'Risk and succession',
  },
};
